import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { showToastMessage } from "common/service/CommonService";
import { useGlobalStudy } from "context/StudyContext";
import { useVolunteerAssignmentService } from "modules/Volunteer/AssigningStudyModule/VolunteerStudyScreeningList/api/useVolunteerAssignmentService";
import { queryClient } from "api/queryClient";
import { AssignStudyColumn } from "../helper/volunteer.constant";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  volunteerId: number | null;
  volunteerName: string | null;
  onAssign?: (data: any) => void;
}

const AssignToStudyModal: React.FC<Props> = ({ isOpen, toggle, volunteerId, volunteerName, onAssign }) => {
  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  // Fetch Studies
  const { DropDownAllQuery: studySelectQuery } = useMasterStudyService({
    id: volunteerId,
    enabledDropDown: true
  });
  const { InsertMutation } = useVolunteerAssignmentService();

  const studyOptions = (studySelectQuery.data as any)?.data?.map((s: any) => ({
    label: s.studycode,
    value: s.studyid,
  })) || [];

  const { selectedStudy: globalStudy } = useGlobalStudy();

  React.useEffect(() => {
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyid)
      );
      if (matched) {
        setValue("studyid", matched.value);
      }
    } else {
      setValue("studyid", "");
    }
  }, [globalStudy?.studyid, studyOptions, setValue]);

  const handleSave = (data: any) => {

    if (!volunteerId) return;
    // Perform assignment logic
    const payload = {
      volunteerid: volunteerId,
      studyid: data.studyid,
    };
    // For now, storing in sessionStorage as per previous app behavior to persist assignment tracking 

    InsertMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["volunteer-selectAll"] });
        reset();
        toggle();
        showToastMessage("success", `Volunteer ${volunteerName} successfully assigned to Study!`);
      },
      onError: (error: any) => {
        showToastMessage("error", error ?? "Failed to assign volunteer to study");
      }
    });

    if (onAssign) onAssign(payload);
  };

  return (
    <Modal isOpen={isOpen}
      toggle={toggle}
      size="md"
      centered
      backdrop="static"
      className="common_modal">
      <ModalHeader toggle={toggle} className="p-3">
        Assign Study to {volunteerName || "Volunteer"}
      </ModalHeader>
      <ModalBody className="p-4">
        <CommonForm
          columns={1}
          control={control}
          errors={errors}
          fields={AssignStudyColumn(studyOptions)
          }
        />
      </ModalBody>
      <ModalFooter className="bg-white border-top">
        <Button color="dark" onClick={toggle} outline>
          Cancel
        </Button>
        <Button color="primary" outline onClick={handleSubmit(handleSave)} disabled={InsertMutation.isPending}> 
          {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
          Assign Study
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignToStudyModal;
